// Distribution chart — the ManCo action ledger for S1 / D1.
//
// Two horizontal stacked bars (TODAY vs TARGET) at the top show the weight
// shift visually. Below, the rows ARE the meeting agenda: each row carries
// its own commitment lifecycle — propose → discuss → agree/disagree →
// commit owner → cite blockers. The table isn't a summary of the strategy,
// it IS the strategy, row by row, that gets turned into committed work.
//
// State model: each row maps to at most one ActionRow with kind="commitment"
// or kind="disagree" (see lib/actions.ts → findRowCommitment). The lifecycle
// is derived from the commitment's status field:
//
//   no commitment              → PROPOSED  (system surfaces target as proposal)
//   kind=commitment, status=doing   → IN FLIGHT
//   kind=commitment, status=blocked → BLOCKED
//   kind=commitment, status=watch   → AGREED (committed, work not yet started)
//   kind=commitment, status=done    → DONE
//   kind=disagree                   → DISAGREED (with reason)

import { useState } from "react";

import {
  STATUS_LABEL as ACTION_STATUS_LABEL,
  type ActionRow,
  type ActionStatus,
  actionTouchesPath,
  addAction,
  deleteAction,
  findRowCommitment,
  updateAction,
} from "../lib/actions";
import {
  CATEGORIES,
  FIT_COLOR,
  FIT_LABEL,
  SIGNUP_BONUS_SOURCE,
  deriveFit,
  type CategoryFit,
} from "../lib/categories";
import { DUE_BY_MAX_OFFSET_DAYS, fmtDueBy, isoInDays, isoToday } from "../lib/due-by";
import { toSharePct, useLiveCategoryStats, type LiveState } from "../lib/live-category-stats";

export type DistributionKind = "earn" | "spend";

type RowState = "proposed" | "agreed" | "flight" | "blocked" | "done" | "disagreed";

interface Row {
  code: string;
  label: string;
  todayPct: number;
  targetPct: number;
  rationale: string;
  fit: CategoryFit | null;
  notInstrumented?: boolean;
  /** The commitment record bound to this row (if any). */
  commitment?: ActionRow;
}

interface Props {
  kind: DistributionKind;
  kpiId: string; // "s1" | "d1" · used as the impact-path prefix
  actions: ActionRow[];
  currentUser: string;
  onCommitmentChanged: () => Promise<void> | void;
}

function buildRows(
  kind: DistributionKind,
  kpiId: string,
  actions: ActionRow[],
  liveTodayByCode: Record<string, number>,
): Row[] {
  const rows: Row[] = [];

  if (kind === "earn") {
    const commitment = findRowCommitment(actions, kpiId, SIGNUP_BONUS_SOURCE.code);
    rows.push({
      code: SIGNUP_BONUS_SOURCE.code,
      label: SIGNUP_BONUS_SOURCE.label,
      todayPct: SIGNUP_BONUS_SOURCE.earnTodayPct,
      targetPct: SIGNUP_BONUS_SOURCE.earnTargetPct,
      rationale: SIGNUP_BONUS_SOURCE.rationale,
      fit: null,
      commitment,
    });
  }

  for (const cat of CATEGORIES) {
    const staticToday = kind === "earn" ? (cat.earnTodayPct ?? 0) : (cat.spendTodayPct ?? 0);
    const liveToday = liveTodayByCode[cat.code];
    const today = liveToday !== undefined ? liveToday : staticToday;
    const target =
      kind === "earn" ? (cat.intent?.earnTargetPct ?? 0) : (cat.intent?.spendTargetPct ?? 0);
    if (today === 0 && target === 0) continue;
    const commitment = findRowCommitment(actions, kpiId, cat.code);
    rows.push({
      code: cat.code,
      label: cat.label,
      todayPct: today,
      targetPct: target,
      rationale:
        cat.intent?.rationale ?? (target === 0 ? "No target weight · held / sunset / pre-test." : ""),
      fit: deriveFit(cat),
      notInstrumented: cat.notInstrumented,
      commitment,
    });
  }

  return rows.sort((a, b) => b.targetPct - a.targetPct);
}

function rowState(commitment: ActionRow | undefined): RowState {
  if (!commitment) return "proposed";
  if (commitment.kind === "disagree") return "disagreed";
  switch (commitment.status) {
    case "doing":
      return "flight";
    case "blocked":
      return "blocked";
    case "watch":
      return "agreed";
    case "done":
      return "done";
    default:
      return "proposed";
  }
}

const STATE_LABEL: Record<RowState, string> = {
  proposed: "PROPOSED",
  agreed: "AGREED",
  flight: "IN FLIGHT",
  blocked: "BLOCKED",
  done: "DONE",
  disagreed: "DISAGREED",
};

const STATE_COLOR: Record<RowState, string> = {
  proposed: "var(--ink-faint)",
  agreed: "#4fb3ff",
  flight: "var(--purple)",
  blocked: "var(--coral)",
  done: "#5ec98c",
  disagreed: "var(--ink-faint)",
};

const COLORS = [
  "#b8b8b8",
  "#5ec98c",
  "#7fd1a8",
  "#4fb3ff",
  "#7ec7e8",
  "#f5c47e",
  "#d99c3a",
  "#c44a4a",
  "#8c5e2b",
  "#a07a52",
];

export function DistributionChart({
  kind,
  kpiId,
  actions,
  currentUser,
  onCommitmentChanged,
}: Props) {
  // Live data: only D1 (spend) has a Phase-3 query today. S1 (earn) stays
  // static — earn is 98% signup_bonus and unlikely to have moved.
  const liveSpend = useLiveCategoryStats(kind === "spend" ? "live-spend-share-by-category" : "");
  const liveTodayByCode = kind === "spend" ? toSharePct(liveSpend.byCode) : {};
  const rows = buildRows(kind, kpiId, actions, liveTodayByCode);
  const colorByCode: Record<string, string> = {};
  rows.forEach((r, i) => (colorByCode[r.code] = COLORS[i % COLORS.length]!));

  const [openCode, setOpenCode] = useState<string | null>(null);

  return (
    <div style={wrap}>
      <div style={head}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
          <div style={heading}>
            {kind === "earn"
              ? "Where earn happens · today vs. target"
              : "Where spend happens · today vs. target"}
          </div>
          {kind === "spend" && <LiveBadge state={liveSpend.state} />}
        </div>
        <div style={subtitle}>
          {kind === "earn"
            ? "Today, 98% of coins are minted at signup. After that, customers never earn again. To build a real loop, earn needs to spread across the 5–6 categories they already use every day, week or month."
            : "Today, 73% of spend is one thing: paying bank-transfer fees with coins. It works · keep it. But spend needs to grow in the same recurring categories where we add earn, so the loop closes."}
        </div>
        <div style={hint}>Click a row to discuss, commit an owner, or disagree.</div>
      </div>

      <BarRow label="TODAY" rows={rows} pctKey="todayPct" colorByCode={colorByCode} />
      <BarRow label="TARGET" rows={rows} pctKey="targetPct" colorByCode={colorByCode} />

      {/* The row-level commitment ledger. Each row is its own ManCo decision. */}
      <div style={tableWrap}>
        <div style={tableHead}>
          <span style={{ ...tableHeadCell, flex: 2.2, textAlign: "left" }}>Category</span>
          <span style={tableHeadCell}>Today</span>
          <span style={tableHeadCell}>Target</span>
          <span style={tableHeadCell} title="Percentage points we need to shift this row by · positive grows, negative shrinks.">Shift</span>
          <span style={tableHeadCell} title="How well this category fits the recurring-earn-and-spend pattern (proven / strong / plausible / target-to-instrument / activation-trap / antipattern).">Viability</span>
          <span style={{ ...tableHeadCell, flex: 1.6 }}>Status</span>
          <span style={{ ...tableHeadCell, flex: 1.4, textAlign: "left" }}>Owner</span>
          <span style={{ ...tableHeadCell, flex: 2.5, textAlign: "left" }}>Rationale</span>
          <span style={{ ...tableHeadCell, flex: 0.4 }} />
        </div>
        {rows.map((r) => (
          <RowItem
            key={r.code}
            row={r}
            kpiId={kpiId}
            colorByCode={colorByCode}
            isOpen={openCode === r.code}
            onToggle={() => setOpenCode((c) => (c === r.code ? null : r.code))}
            currentUser={currentUser}
            onCommitmentChanged={onCommitmentChanged}
          />
        ))}
      </div>
    </div>
  );
}

// ---------------------- Live-data state badge ----------------------------

function LiveBadge({ state }: { state: LiveState }) {
  const cfg: Record<LiveState, { label: string; color: string }> = {
    loading: { label: "LOADING", color: "var(--ink-faint)" },
    live: { label: "LIVE", color: "#5ec98c" },
    fallback: { label: "SNAPSHOT", color: "var(--amber)" },
    error: { label: "ERROR", color: "var(--coral)" },
  };
  const { label, color } = cfg[state];
  return (
    <span
      title={
        state === "live"
          ? "Numbers pulled live from Metabase (coinsLedger, 365d)"
          : state === "fallback"
            ? "Showing 12 May 2026 snapshot — live query unavailable in this context"
            : state === "loading"
              ? "Fetching live numbers from Metabase…"
              : "Live query errored — showing snapshot fallback"
      }
      style={{
        fontFamily: "var(--font-mono)",
        fontSize: 9,
        fontWeight: 700,
        letterSpacing: "0.12em",
        color,
        border: `1px solid ${color}66`,
        background: `${color}22`,
        padding: "1px 6px",
        borderRadius: 3,
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </span>
  );
}

// ---------------------- Row + inline form --------------------------------

function RowItem({
  row,
  kpiId,
  colorByCode,
  isOpen,
  onToggle,
  currentUser,
  onCommitmentChanged,
}: {
  row: Row;
  kpiId: string;
  colorByCode: Record<string, string>;
  isOpen: boolean;
  onToggle: () => void;
  currentUser: string;
  onCommitmentChanged: () => Promise<void> | void;
}) {
  const state = rowState(row.commitment);
  const delta = row.targetPct - row.todayPct;
  const deltaText =
    Math.abs(delta) < 0.5 ? "—" : (delta > 0 ? "+" : "") + delta.toFixed(0) + "pp";
  const deltaColor =
    Math.abs(delta) < 0.5
      ? "var(--ink-faint)"
      : delta > 0
        ? "#5ec98c"
        : "var(--coral)";
  const disagreed = state === "disagreed";
  const [hover, setHover] = useState(false);
  const hasBlocker = !!row.commitment?.blockers?.trim();

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <button
        type="button"
        onClick={onToggle}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        title={state === "proposed" ? "Click to discuss / commit / disagree" : "Click to edit commitment"}
        style={{
          ...tableRow,
          textAlign: "left",
          background: isOpen
            ? "var(--bg-1, rgba(255,255,255,0.06))"
            : hover
              ? "rgba(83, 74, 183, 0.08)"
              : "transparent",
          opacity: disagreed ? 0.55 : 1,
          cursor: "pointer",
          width: "100%",
          border: "none",
          borderLeft: `3px solid ${isOpen ? "var(--purple)" : "transparent"}`,
          color: "inherit",
          transition: "background 120ms ease",
        }}
      >
        <span style={{ ...tableCell, flex: 2.2, display: "flex", alignItems: "center", gap: 8, textAlign: "left" }}>
          <span style={{ ...swatch, background: colorByCode[row.code] }} />
          <span
            style={{
              fontWeight: 600,
              color: "var(--ink)",
              textDecoration: disagreed ? "line-through" : "none",
            }}
          >
            {row.label}
          </span>
          {row.notInstrumented && <span style={notInstrumentedChip}>not instrumented</span>}
        </span>
        <span style={tableCell}>{row.todayPct.toFixed(1)}%</span>
        <span style={{ ...tableCell, fontWeight: 700 }}>{row.targetPct.toFixed(0)}%</span>
        <span style={{ ...tableCell, color: deltaColor, fontWeight: 700 }}>{deltaText}</span>
        <span style={tableCell}>
          {row.fit && <span style={fitChip(row.fit)}>{FIT_LABEL[row.fit]}</span>}
        </span>
        <span style={{ ...tableCell, flex: 1.6, display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 3 }}>
          <span style={stateChip(state)}>{STATE_LABEL[state]}</span>
          {row.commitment?.dueBy && (
            <span style={dueChip(row.commitment.dueBy)} title={`Due ${fmtDueBy(row.commitment.dueBy)}`}>⏱ due {fmtDueBy(row.commitment.dueBy)}</span>
          )}
          {hasBlocker && (
            <span style={blockerChip} title={row.commitment!.blockers}>
              ✕ {row.commitment!.blockers}
            </span>
          )}
        </span>
        <span
          style={{
            ...tableCell,
            flex: 1.4,
            textAlign: "left",
            color: row.commitment?.owner ? "var(--ink)" : "var(--ink-faint)",
            fontWeight: row.commitment?.owner ? 600 : 400,
          }}
        >
          {row.commitment?.owner || "—"}
        </span>
        <span style={{ ...tableCell, flex: 2.5, textAlign: "left", color: "var(--ink-dim)", fontSize: 11.5, lineHeight: 1.5 }}>
          {row.rationale}
        </span>
        <span style={{ ...tableCell, flex: 0.4, textAlign: "center" }}>
          <span style={chevron(isOpen, state === "proposed" && hover)}>
            {isOpen ? "▾" : "▸"}
          </span>
        </span>
      </button>

      {isOpen && (
        <RowEditor
          row={row}
          kpiId={kpiId}
          currentUser={currentUser}
          onClose={onToggle}
          onCommitmentChanged={onCommitmentChanged}
        />
      )}
    </div>
  );
}

function RowEditor({
  row,
  kpiId,
  currentUser,
  onClose,
  onCommitmentChanged,
}: {
  row: Row;
  kpiId: string;
  currentUser: string;
  onClose: () => void;
  onCommitmentChanged: () => Promise<void> | void;
}) {
  const existing = row.commitment;
  const [owner, setOwner] = useState(existing?.owner ?? "");
  const [doing, setDoing] = useState(existing?.doing ?? "");
  const [blockers, setBlockers] = useState(existing?.blockers ?? "");
  const [dueBy, setDueBy] = useState(existing?.dueBy ?? "");
  const [status, setStatus] = useState<ActionStatus>(existing?.status ?? "watch");
  const [saving, setSaving] = useState(false);

  const focusPath = `${kpiId}:${row.code}`;
  const minDate = isoToday();
  const maxDate = isoInDays(DUE_BY_MAX_OFFSET_DAYS);
  const title = `Commit ${row.label} to ${row.targetPct.toFixed(0)}% target`;

  async function save(kind: "commitment" | "disagree") {
    setSaving(true);
    try {
      if (existing) {
        await updateAction(existing.id, {
          kind,
          owner,
          doing,
          blockers,
          dueBy,
          status: kind === "disagree" ? "watch" : status,
          title: kind === "disagree" ? `Disagree · ${row.label}` : title,
        });
      } else {
        await addAction({
          title: kind === "disagree" ? `Disagree · ${row.label}` : title,
          impacts: [focusPath],
          owner,
          status: kind === "disagree" ? "watch" : status,
          blockers,
          doing,
          dueBy,
          author: currentUser,
          kind,
        });
      }
      await onCommitmentChanged();
      onClose();
    } finally {
      setSaving(false);
    }
  }

  async function clearCommitment() {
    if (!existing) {
      onClose();
      return;
    }
    setSaving(true);
    try {
      await deleteAction(existing.id);
      await onCommitmentChanged();
      onClose();
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={editorWrap}>
      <div style={editorHead}>
        <span style={editorEyebrow}>
          {existing ? "EDIT COMMITMENT" : "PROPOSE COMMITMENT"} · {row.label} · {row.todayPct.toFixed(1)}% → {row.targetPct.toFixed(0)}%
        </span>
        <span style={editorRationale}>{row.rationale}</span>
      </div>

      <div style={editorGrid}>
        <label style={editorField}>
          <span style={editorLabel}>Status</span>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as ActionStatus)}
            style={editorInput}
          >
            <option value="watch">Agreed · not yet started</option>
            <option value="doing">In flight</option>
            <option value="blocked">Blocked</option>
            <option value="done">Done · target hit</option>
          </select>
        </label>

        <label style={editorField}>
          <span style={editorLabel}>Owner</span>
          <input
            value={owner}
            onChange={(e) => setOwner(e.target.value)}
            placeholder="Who is committed?"
            style={editorInput}
          />
        </label>
        <label style={editorField}><span style={editorLabel}>By when (max 30 days)</span>
          <input type="date" value={dueBy} min={minDate} max={maxDate} onChange={(e) => setDueBy(e.target.value)} style={editorInput} />
        </label>

        <label style={{ ...editorField, gridColumn: "span 2" }}>
          <span style={editorLabel}>Decision / what we're doing</span>
          <input
            value={doing}
            onChange={(e) => setDoing(e.target.value)}
            placeholder="ManCo decision: e.g. 'Agreed Card at 22% via UBA earn-mint endpoint'"
            style={editorInput}
          />
        </label>

        <label style={{ ...editorField, gridColumn: "span 2" }}>
          <span style={editorLabel}>Blocker (if any)</span>
          <input
            value={blockers}
            onChange={(e) => setBlockers(e.target.value)}
            placeholder="What's in the way?"
            style={editorInput}
          />
        </label>
      </div>

      <div style={editorActions}>
        <button
          type="button"
          onClick={() => save("commitment")}
          disabled={saving || !owner.trim()}
          style={btnPrimary}
        >
          {existing && existing.kind !== "disagree" ? "Update commitment" : "Commit"}
        </button>
        <button
          type="button"
          onClick={() => save("disagree")}
          disabled={saving}
          style={btnGhost}
        >
          {existing?.kind === "disagree" ? "Update disagree" : "Disagree"}
        </button>
        {existing && (
          <button
            type="button"
            onClick={clearCommitment}
            disabled={saving}
            style={btnDanger}
          >
            Clear
          </button>
        )}
        <span style={{ flex: 1 }} />
        <button type="button" onClick={onClose} disabled={saving} style={btnGhost}>
          Close
        </button>
      </div>
    </div>
  );
}

// ---------------------- bars -----------------------------------------------

function BarRow({
  label,
  rows,
  pctKey,
  colorByCode,
}: {
  label: "TODAY" | "TARGET";
  rows: Row[];
  pctKey: "todayPct" | "targetPct";
  colorByCode: Record<string, string>;
}) {
  const total = rows.reduce((s, r) => s + r[pctKey], 0);
  const segments = rows.filter((r) => r[pctKey] > 0);
  return (
    <div style={barOuter}>
      <span style={barLabel}>{label}</span>
      <div style={barTrack}>
        {segments.map((r) => {
          const pct = total > 0 ? (r[pctKey] / total) * 100 : 0;
          return (
            <div
              key={r.code}
              title={`${r.label}: ${r[pctKey].toFixed(1)}%`}
              style={{
                width: `${pct}%`,
                background: colorByCode[r.code],
                minWidth: pct > 0 ? 2 : 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "rgba(0,0,0,0.7)",
                fontSize: 10,
                fontWeight: 700,
                fontVariantNumeric: "tabular-nums",
                overflow: "hidden",
                whiteSpace: "nowrap",
              }}
            >
              {pct >= 7 ? Math.round(r[pctKey]) + "%" : ""}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------- styles --------------------------------------------

const wrap: React.CSSProperties = {
  padding: "16px 18px",
  borderTop: "1px solid var(--edge)",
  borderBottom: "1px solid var(--edge)",
  background: "var(--panel-1, rgba(255,255,255,0.02))",
  display: "flex",
  flexDirection: "column",
  gap: 12,
};
const head: React.CSSProperties = { display: "flex", flexDirection: "column", gap: 4 };
const heading: React.CSSProperties = { fontSize: 13, fontWeight: 700, color: "var(--ink)" };
const subtitle: React.CSSProperties = {
  fontSize: 11.5,
  color: "var(--ink-faint)",
  lineHeight: 1.5,
};
const hint: React.CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontSize: 9.5,
  letterSpacing: "0.1em",
  color: "var(--purple)",
  fontWeight: 700,
  textTransform: "uppercase",
  marginTop: 4,
};
const barOuter: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "64px 1fr",
  alignItems: "center",
  gap: 10,
};
const barLabel: React.CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontSize: 10,
  letterSpacing: "0.14em",
  color: "var(--ink-faint)",
  fontWeight: 700,
};
const barTrack: React.CSSProperties = {
  display: "flex",
  height: 26,
  borderRadius: 4,
  overflow: "hidden",
  background: "var(--edge)",
  border: "1px solid var(--edge)",
};
const tableWrap: React.CSSProperties = {
  marginTop: 6,
  display: "flex",
  flexDirection: "column",
  border: "1px solid var(--edge)",
  borderRadius: 4,
};
const tableHead: React.CSSProperties = {
  display: "flex",
  gap: 8,
  padding: "8px 10px",
  borderBottom: "1px solid var(--edge)",
  background: "var(--bg-1, rgba(255,255,255,0.03))",
};
const tableHeadCell: React.CSSProperties = {
  flex: 1,
  fontFamily: "var(--font-mono)",
  fontSize: 9,
  letterSpacing: "0.08em",
  color: "var(--ink-faint)",
  fontWeight: 700,
  textTransform: "uppercase",
  textAlign: "right",
};
const tableRow: React.CSSProperties = {
  display: "flex",
  gap: 8,
  padding: "9px 10px",
  borderBottom: "1px solid var(--edge)",
  alignItems: "center",
};
const tableCell: React.CSSProperties = {
  flex: 1,
  fontSize: 11.5,
  color: "var(--ink-dim)",
  fontVariantNumeric: "tabular-nums",
  textAlign: "right",
};
const swatch: React.CSSProperties = { width: 10, height: 10, borderRadius: 2, flexShrink: 0 };
const notInstrumentedChip: React.CSSProperties = {
  fontSize: 9,
  letterSpacing: "0.08em",
  fontFamily: "var(--font-mono)",
  color: "#4fb3ff",
  background: "rgba(79, 179, 255, 0.14)",
  padding: "1px 5px",
  borderRadius: 3,
  fontWeight: 700,
  textTransform: "uppercase",
};
function fitChip(fit: CategoryFit): React.CSSProperties {
  const color = FIT_COLOR[fit];
  return {
    display: "inline-block",
    fontFamily: "var(--font-mono)",
    fontSize: 8.5,
    fontWeight: 700,
    letterSpacing: "0.08em",
    color,
    border: `1px solid ${color}`,
    borderRadius: 3,
    padding: "1px 5px",
  };
}
function stateChip(state: RowState): React.CSSProperties {
  const color = STATE_COLOR[state];
  return {
    display: "inline-block",
    fontFamily: "var(--font-mono)",
    fontSize: 9,
    fontWeight: 700,
    letterSpacing: "0.1em",
    color,
    background: `${color}22`,
    border: `1px solid ${color}66`,
    padding: "2px 7px",
    borderRadius: 3,
    whiteSpace: "nowrap",
  };
}
const blockerChip: React.CSSProperties = {
  display: "inline-block",
  maxWidth: 220,
  fontSize: 10,
  color: "var(--coral)",
  background: "rgba(196, 74, 74, 0.12)",
  border: "1px solid rgba(196, 74, 74, 0.4)",
  padding: "1px 6px",
  borderRadius: 3,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  fontStyle: "italic",
};
function chevron(isOpen: boolean, emphasised: boolean): React.CSSProperties {
  return {
    display: "inline-block",
    fontSize: 13,
    color: isOpen ? "var(--purple)" : emphasised ? "var(--purple)" : "var(--ink-faint)",
    fontWeight: 700,
    transition: "transform 120ms ease, color 120ms ease",
  };
}

// editor styles
const editorWrap: React.CSSProperties = {
  padding: "14px 16px 16px",
  background: "var(--bg-1, rgba(255,255,255,0.04))",
  borderBottom: "1px solid var(--edge)",
  display: "flex",
  flexDirection: "column",
  gap: 12,
};
const editorHead: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 4,
};
const editorEyebrow: React.CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontSize: 10,
  letterSpacing: "0.12em",
  color: "var(--ink-faint)",
  fontWeight: 700,
};
const editorRationale: React.CSSProperties = {
  fontSize: 11.5,
  color: "var(--ink-dim)",
  fontStyle: "italic",
};
const editorGrid: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: 10,
};
const editorField: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 4,
};
const editorLabel: React.CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontSize: 9,
  letterSpacing: "0.1em",
  color: "var(--ink-faint)",
  fontWeight: 700,
  textTransform: "uppercase",
};
const editorInput: React.CSSProperties = {
  background: "var(--panel-2)",
  border: "1px solid var(--edge)",
  borderRadius: 3,
  padding: "6px 8px",
  color: "var(--ink)",
  fontSize: 12,
  fontFamily: "inherit",
};
const editorActions: React.CSSProperties = {
  display: "flex",
  gap: 8,
  alignItems: "center",
};
const btnPrimary: React.CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontSize: 10,
  letterSpacing: "0.1em",
  fontWeight: 700,
  textTransform: "uppercase",
  padding: "6px 12px",
  borderRadius: 3,
  border: "1px solid var(--purple)",
  background: "var(--purple)",
  color: "white",
  cursor: "pointer",
};
const btnGhost: React.CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontSize: 10,
  letterSpacing: "0.1em",
  fontWeight: 700,
  textTransform: "uppercase",
  padding: "6px 12px",
  borderRadius: 3,
  border: "1px solid var(--edge)",
  background: "transparent",
  color: "var(--ink-dim)",
  cursor: "pointer",
};
const btnDanger: React.CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontSize: 10,
  letterSpacing: "0.1em",
  fontWeight: 700,
  textTransform: "uppercase",
  padding: "6px 12px",
  borderRadius: 3,
  border: "1px solid var(--coral)",
  background: "transparent",
  color: "var(--coral)",
  cursor: "pointer",
};

function dueChip(iso: string): React.CSSProperties {
  const tone = (() => {
    const d = new Date(iso + "T00:00:00").getTime();
    const today = new Date(new Date().toISOString().slice(0, 10) + "T00:00:00").getTime();
    const days = Math.round((d - today) / 86_400_000);
    if (days < 0) return "var(--coral)";
    if (days <= 7) return "var(--amber)";
    return "#4fb3ff";
  })();
  return {
    display: "inline-block", fontFamily: "var(--font-mono)", fontSize: 9,
    fontWeight: 700, letterSpacing: "0.08em", color: tone,
    background: `${tone}22`, border: `1px solid ${tone}66`,
    padding: "1px 6px", borderRadius: 3, whiteSpace: "nowrap",
  };
}

